import React, { useEffect, useState } from 'react'
import { ParkingSpot, ParkingDetail } from '../../services/mapsServiceNew'
import { mapsService } from '../../services/mapsServiceNew'
import { useLocalization } from '../../contexts/LocalizationContext'
import { Navigation, MapPin, Clock, X } from 'lucide-react'

interface ParkingInfoPanelProps {
  parking: ParkingSpot | null
  onClose: () => void
}

export function ParkingInfoPanel({ parking, onClose }: ParkingInfoPanelProps) {
  const { t } = useLocalization()
  const [detail, setDetail] = useState<ParkingDetail | null>(null)

  useEffect(() => {
    if (!parking) {
      setDetail(null)
      return
    }

    // Détails pré-cachés lors de la recherche — lecture instantanée
    mapsService.fetchParkingDetail(parking.place_id).then(setDetail)
  }, [parking?.place_id])

  if (!parking) return null

  const handleNavigate = () => {
    mapsService.openDirections(parking)
  }

  const formatDistance = (distance?: number) => {
    if (!distance) return ''
    if (distance < 1000) return `${distance}m`
    return `${(distance / 1000).toFixed(1)}km`
  }

  // Nom enrichi depuis le détail ou fallback sur le spot
  const displayName = detail?.name || detail?.operator
    ? (detail.name || `${detail.operator} Parking`)
    : parking.name

  // Adresse enrichie depuis le détail
  const displayAddress = detail?.address || parking.address

  // Prix depuis le détail
  const getPriceText = () => {
    if (!detail) return null
    if (detail.fee === 'no' || detail.fee === 'false') return t('parking.priceLevel.free')
    if (detail.fee === 'yes' || detail.fee === 'true') return t('parking.priceLevel.moderate')
    return null
  }

  // Tags d'infos enrichies
  const detailTags: string[] = []
  if (detail) {
    if (detail.capacity) detailTags.push(`${detail.capacity} places`)
    if (detail.parkingType) detailTags.push(detail.parkingType)
    if (detail.access) detailTags.push(`Access: ${detail.access}`)
    if (detail.surface) detailTags.push(detail.surface)
    if (detail.covered) detailTags.push('Covered')
    if (detail.lit) detailTags.push('Lit')
    if (detail.surveillance) detailTags.push('Surveillance')
    if (detail.maxstay) detailTags.push(`Max: ${detail.maxstay}`)
    if (detail.operator) detailTags.push(detail.operator)
  }

  const priceText = getPriceText()

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-auto md:min-w-[480px] bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-in-out" style={{ zIndex: 10003 }}>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 pr-4">
            {/* Badges */}
            {(priceText || detail?.openingHoursRaw) && (
              <div className="flex items-center space-x-2 mb-1.5">
                {priceText && (
                  <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {priceText}
                  </span>
                )}
                {detail?.openingHoursRaw && (
                  <div className="flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-100 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-blue-600 animate-pulse"></div>
                    {t('parking.status.open')}
                  </div>
                )}
              </div>
            )}
            <h3 className="text-[22px] font-black text-slate-900 leading-tight tracking-tight mb-1">
              {displayName}
            </h3>
            <p className="text-[13px] text-slate-500 font-medium flex items-start">
              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{displayAddress}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-slate-100/80 hover:bg-slate-200 text-slate-500 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {parking.distance && (
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 flex flex-col justify-center items-center text-center">
              <Navigation className="w-5 h-5 text-emerald-500 mb-1" />
              <span className="text-[15px] font-black text-slate-800">{formatDistance(parking.distance)}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Distance</span>
            </div>
          )}
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 flex flex-col justify-center items-center text-center">
            <Clock className="w-5 h-5 text-blue-500 mb-1" />
            <span className="text-[15px] font-black text-slate-800">
              {detail?.openingHoursRaw || '24/7'}
            </span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Horaires</span>
          </div>
        </div>

        {/* Detail tags */}
        {detailTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {detailTags.map((tag) => (
              <span key={tag} className="bg-slate-100 text-slate-600 text-[11px] font-bold px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Action Button — toujours visible */}
        <button
          onClick={handleNavigate}
          className="w-full bg-emerald-500 text-white py-4 px-4 rounded-[1.25rem] font-black text-[17px] shadow-lg shadow-emerald-500/30 hover:bg-emerald-600 hover:shadow-emerald-500/40 transition-all active:scale-[0.98] flex items-center justify-center"
        >
          <Navigation className="w-5 h-5 mr-2" />
          {t('parking.getDirections')}
        </button>
      </div>
    </div>
  )
}
